import { useMemo } from 'react';
import { WEEKDAYS_SHORT, addDays, describeFrequency, fmtShort, localToday, occursOn, parseISODate, startOfWeek, statusName } from '@quadro/domain';
import { Icon } from '../../components/Icon';
import { useHolidays, useRoutines, useTasks } from '../../data/queries';
import { Page } from '../../layout/Page';
import { openSheet } from '../../state/sheets';
import { useWorkspace } from '../../state/workspace';

export function useHolidayCheck(from: string, to: string) {
  const h = useHolidays(from, to).data;
  return useMemo(() => { const s = new Set(h ?? []); return (d: string) => s.has(d); }, [h]);
}

/** Mapa de rotinas: as próximas 4 semanas de cada atividade recorrente. */
export function RoutinesPage() {
  const ws = useWorkspace();
  const routines = useRoutines().data ?? [];
  const tasks = useTasks().data ?? [];
  const today = localToday();
  const start = startOfWeek(today);
  const days = [...Array(28)].map((_, i) => addDays(start, i));
  const isHoliday = useHolidayCheck(days[0], days[27]);
  const sorted = [...routines].sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title));
  const active = sorted.filter(r => r.active);
  const byOccurrence = new Map(tasks.filter(t => t.routineId).map(t => [`${t.routineId}|${t.routineDate}`, t]));

  const toolbar = <>
    <div className="tb-title"><h1>Rotinas</h1><span className="sub">o que se repete na área</span></div><div className="tb-space" />
    <div className="tools">{ws.canWrite && <button className="btn primary" onClick={() => openSheet({ kind: 'routine', id: null })}><Icon name="plus" /> Nova rotina</button>}</div>
  </>;

  if (!sorted.length) {
    return <Page toolbar={toolbar}><div className="rot"><div className="rot-empty"><h2>Mapeie o que se repete</h2>
      <p>Relatórios semanais, fechamentos do mês, conferências diárias. Cadastre cada rotina uma vez e o Quadro cria a tarefa no dia certo, para a pessoa certa.</p>
      {ws.canWrite && <button className="btn primary" onClick={() => openSheet({ kind: 'routine', id: null })}><Icon name="plus" /> Nova rotina</button>}
    </div></div></Page>;
  }

  const future = active.reduce((n, r) => n + days.filter(d => d >= today && occursOn(r, d, isHoliday)).length, 0);
  const load: Record<string, number> = {};
  active.forEach(r => { if (r.assigneeId) load[r.assigneeId] = (load[r.assigneeId] || 0) + days.filter(d => d >= today && occursOn(r, d, isHoliday)).length; });
  const top = Object.entries(load).sort((a, b) => b[1] - a[1])[0];
  const dayClass = (d: string) => { const w = parseISODate(d).getDay(); return `rm-d ${w === 0 ? 'ws' : ''} ${w === 0 || w === 6 ? 'we' : ''} ${d === today ? 'hj' : ''}`; };

  return (
    <Page toolbar={toolbar}>
      <div className="rot">
        <div className="rot-intro">
          <div><h1>Mapa de rotinas</h1><p>As próximas quatro semanas das atividades recorrentes da área. Cada rotina vira uma tarefa no quadro na data certa; dias úteis seguem o calendário ANBIMA.</p></div>
          <div className="rot-stats">
            <div><b>{active.length}</b><span>rotinas ativas</span></div>
            <div><b>{future}</b><span>ocorrências até {fmtShort(days[27])}</span></div>
            {top && <div><b>{top[1]}</b><span>com {ws.firstName(top[0])}, maior carga</span></div>}
          </div>
        </div>
        <div className="rmap"><div className="rm-grid">
          <div className="rm-row head"><div className="rm-name rm-lab">Rotina</div><div className="rm-days">
            {days.map(d => { const x = parseISODate(d); return <span key={d} className={dayClass(d)}><b>{WEEKDAYS_SHORT[x.getDay()][0]}</b>{x.getDate()}</span>; })}
          </div></div>
          {sorted.map(r => {
            const p = ws.settings.projects.find(x => x.id === r.projectId);
            return (
              <div key={r.id} className={`rm-row ${r.active ? '' : 'off'}`}>
                <button className="rm-name" onClick={() => openSheet({ kind: 'routine', id: r.id })}>
                  <span className="rm-t">{p && <i style={{ background: p.color }} />}{r.title}{!r.active && <span className="muted"> (pausada)</span>}</span>
                  <span className="rm-f">{describeFrequency(r.frequency, r.startsOn)}{r.assigneeId ? ` · ${ws.firstName(r.assigneeId)}` : ''}</span>
                </button>
                <div className="rm-days">{days.map(d => {
                  if (!occursOn(r, d, isHoliday)) return <span key={d} className={dayClass(d)} />;
                  const k = byOccurrence.get(`${r.id}|${d}`), tip = `${fmtShort(d)}: `;
                  const open = (id: string) => openSheet({ kind: 'task', id });
                  let dot;
                  if (k && k.status === 'done') dot = <button className="dotp done" title={`${tip}feita`} aria-label={`${tip}feita`} onClick={() => open(k.id)}><Icon name="tick" /></button>;
                  else if (k && d < today) dot = <button className="dotp late" title={`${tip}atrasada, em ${statusName(k.status)}`} aria-label={`${tip}atrasada`} onClick={() => open(k.id)} />;
                  else if (k) dot = <button className="dotp gen" title={`${tip}no quadro, em ${statusName(k.status)}`} aria-label={`${tip}no quadro`} onClick={() => open(k.id)} />;
                  else if (d < today) dot = <span className="dotp miss" title={`${tip}não gerada`} />;
                  else dot = <span className="dotp" title={`${tip}prevista`} />;
                  return <span key={d} className={dayClass(d)}>{dot}</span>;
                })}</div>
              </div>
            );
          })}
        </div></div>
        <div className="rm-legend">
          <span><i className="dotp" />Prevista</span><span><i className="dotp gen" />Já está no quadro</span>
          <span><i className="dotp done"><Icon name="tick" /></i>Feita</span><span><i className="dotp late" />Atrasada</span><span><i className="dotp miss" />Não gerada</span>
        </div>
      </div>
    </Page>
  );
}
